(() => {
  const elementName = 'gh-recent-products';
  if (customElements.get(elementName)) return;

  const makeElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const swatchColors = [
    ['black', '#171717'], ['white', '#f8f8f8'], ['navy', '#16243c'],
    ['blue', '#245b91'], ['grey', '#888b8d'], ['gray', '#888b8d'],
    ['red', '#c63835'], ['orange', '#f58220'], ['neon', '#c8f23d'],
    ['green', '#4f7a46'], ['beige', '#cbb892'], ['tan', '#cbb892'],
    ['brown', '#795548'], ['pink', '#dc8aa0'], ['purple', '#76518f'],
    ['yellow', '#efcf38']
  ];

  class GhRecentProducts extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.loadProducts();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    getRecentHandles() {
      const currentHandle = this.dataset.currentHandle;
      const limit = Number.parseInt(this.dataset.limit, 10) || 8;
      const themeName = window.Shopify?.theme?.name;
      let handles = [];

      try {
        if (themeName) {
          const stored = JSON.parse(localStorage.getItem(`${themeName}:recent_products`) || '[]');
          if (Array.isArray(stored)) handles = stored;
        }
      } catch (error) {
        // Private browsing or malformed old storage should not break the page.
      }

      handles = [...new Set(handles.filter(handle => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(handle)))];
      if (currentHandle) {
        handles = handles.filter(handle => handle !== currentHandle);
        handles.push(currentHandle);
      }

      handles = handles.slice(-Math.max(limit, 8));
      try {
        if (themeName) localStorage.setItem(`${themeName}:recent_products`, JSON.stringify(handles));
      } catch (error) {
        // The carousel can still show the current product without storage.
      }

      return handles.slice(-limit).reverse();
    }

    async loadProducts() {
      const handles = this.getRecentHandles();
      const track = this.querySelector('[data-gh-category-track]');
      if (!track || !handles.length) return;

      const routeRoot = window.Shopify?.routes?.root || '/';
      const root = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
      const products = await Promise.all(handles.map(async handle => {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
            credentials: 'same-origin',
            signal: this.controller.signal
          });
          if (!response.ok) return null;
          return await response.json();
        } catch (error) {
          return null;
        }
      }));

      if (!this.isConnected || this.controller.signal.aborted) return;
      const availableProducts = products.filter(product => product?.handle && product?.title);
      if (!availableProducts.length) return;

      const fragment = document.createDocumentFragment();
      availableProducts.forEach((product, index) => {
        fragment.append(this.makeSlide(product, index + 1, availableProducts.length, root));
      });

      track.replaceChildren(fragment);
      this.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => this.querySelector('gh-category-slider')?.refresh?.());
    }

    formatPrice(priceInCents) {
      const value = Number(priceInCents) / 100;
      const formatted = new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: Number.isInteger(value) ? 0 : 2
      }).format(value);
      return `${this.dataset.currencySymbol || '₹'}${formatted}`;
    }

    makeSlide(product, position, total, root) {
      const url = `${root}products/${encodeURIComponent(product.handle)}`;
      const variant = product.variants?.find(item => item.available) || product.variants?.[0];
      const price = Number(variant?.price ?? product.price ?? 0);
      const comparePrice = Number(variant?.compare_at_price ?? 0);
      const hasDiscount = comparePrice > price;

      const slide = makeElement('li', 'gh-featured-products__slide');
      slide.setAttribute('data-gh-category-slide', '');
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${position} / ${total}: ${product.title}`);

      const article = makeElement('article', 'gh-featured-product-card');
      const media = makeElement('a', 'gh-featured-product-card__media');
      media.href = url;
      media.setAttribute('aria-label', product.title);

      const imageSource = product.featured_image?.src || product.featured_image || product.images?.[0]?.src || product.images?.[0];
      if (imageSource) {
        const image = makeElement('img', 'gh-featured-product-card__image');
        const imageUrl = new URL(imageSource, window.location.href);
        const sizedUrl = width => {
          const url = new URL(imageUrl);
          url.searchParams.set('width', String(width));
          return url.href;
        };
        image.src = sizedUrl(720);
        image.srcset = [240, 320, 400, 500, 600, 720, 900]
          .map(width => `${sizedUrl(width)} ${width}w`).join(', ');
        image.sizes = '(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw';
        image.loading = 'lazy';
        image.alt = product.title;
        media.append(image);
      } else {
        const placeholder = this.querySelector('[data-gh-recent-placeholder]')?.content.firstElementChild;
        if (placeholder) media.append(placeholder.cloneNode(true));
      }
      const plus = makeElement('span', 'gh-featured-product-card__mobile-plus', '+');
      plus.setAttribute('aria-hidden', 'true');
      media.append(plus);
      article.append(media);

      const details = makeElement('div', 'gh-featured-product-card__details');
      if (this.dataset.showProductType === 'true' && product.type) {
        details.append(makeElement('p', 'gh-featured-product-card__type', product.type));
      }

      const titleLink = makeElement('a', 'gh-featured-product-card__title-link');
      titleLink.href = url;
      titleLink.append(makeElement('h3', 'gh-featured-product-card__title', product.title));
      details.append(titleLink);

      const prices = makeElement('div', 'gh-featured-product-card__prices');
      prices.append(makeElement('span', 'gh-featured-product-card__price', this.formatPrice(price)));
      if (hasDiscount) {
        prices.append(makeElement('s', 'gh-featured-product-card__compare-price', this.formatPrice(comparePrice)));
        if (this.dataset.showDiscount === 'true') {
          const discount = Math.floor((comparePrice - price) * 100 / comparePrice);
          prices.append(makeElement('span', 'gh-featured-product-card__discount', `${discount}% OFF`));
        }
      }
      details.append(prices);

      if (this.dataset.showSwatches === 'true') {
        const colorOptionIndex = product.options?.findIndex(option => {
          const name = typeof option === 'string' ? option : option.name;
          return /colou?r/i.test(name || '');
        }) ?? -1;
        if (colorOptionIndex >= 0) {
          const swatches = makeElement('div', 'gh-featured-product-card__swatches');
          const option = product.options[colorOptionIndex];
          swatches.setAttribute('aria-label', typeof option === 'string' ? option : option.name);
          const values = [...new Set((product.variants || []).map(item =>
            item.options?.[colorOptionIndex] || item[`option${colorOptionIndex + 1}`]
          ).filter(Boolean))].slice(0, 4);
          values.forEach(value => {
            const swatch = makeElement('span', 'gh-featured-product-card__swatch');
            swatch.title = value;
            swatch.setAttribute('aria-label', value);
            swatch.style.backgroundColor = swatchColors.find(([name]) => value.toLowerCase().includes(name))?.[1] || '#c8c8c8';
            swatches.append(swatch);
          });
          if (values.length) details.append(swatches);
        }
      }

      article.append(details);
      slide.append(article);
      return slide;
    }
  }

  customElements.define(elementName, GhRecentProducts);
})();
